import { isApiError } from '@/api/http/errors';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import { Button, DetailPage, EmptyState, Skeleton } from '@mgrvtechgroup/admin-ds';
import { Building2Icon } from 'lucide-react';
import { Link } from 'react-router';

/** 기본정보의 섹션별 행 수(건물 및 위치·소개 및 안내·운영 정보) — 받아 온 뒤 본문이 이 틀 그대로 채워진다. */
const SKELETON_ROW_COUNTS = [4, 2, 7] as const;

/**
 * 지점 정보를 받는 동안의 자리 표시. 한 줄 스피너를 두면 받은 뒤 본문이 24px 어긋나고 높이가 수십 배로 튄다.
 * 본문과 같은 섹션·행 틀을 스켈레톤으로 먼저 깔아 둔다(DS: 300ms가 넘는 로딩의 자리 표시는 Skeleton).
 */
export function PropertyDetailSkeleton() {
  return (
    <>
      <LoadingAnnouncement>지점 정보를 불러오고 있습니다.</LoadingAnnouncement>
      <div aria-hidden className="flex min-w-0 flex-col gap-6">
        {SKELETON_ROW_COUNTS.map((rows, section) => (
          <DetailPage.Section key={section}>
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-28" />
            </div>
            <div className="flex flex-col gap-3">
              {Array.from({ length: rows }, (_, row) => (
                <div
                  key={row}
                  className="grid h-5 grid-cols-[7rem_minmax(0,1fr)] items-center gap-x-4"
                >
                  <Skeleton className="h-3.5 w-16" />
                  <Skeleton className="h-3.5 w-2/3" />
                </div>
              ))}
            </div>
          </DetailPage.Section>
        ))}
      </div>
    </>
  );
}

/** 객실 목록(검색·표)을 받는 동안의 자리 표시. */
export function RoomCatalogSkeleton() {
  return (
    <DetailPage.Section title="객실">
      <LoadingAnnouncement>객실 목록을 불러오고 있습니다.</LoadingAnnouncement>
      <div aria-hidden className="flex flex-col gap-2">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    </DetailPage.Section>
  );
}
export function PropertyInvalidAddress({
  to = '/properties',
  label = '목록 처음으로',
}: {
  to?: string;
  label?: string;
}) {
  return (
    <div className="p-6">
      <EmptyState
        icon={Building2Icon}
        title="열려는 주소가 올바르지 않습니다"
        description="목록으로 돌아가 다시 열어 주세요."
      />
      <Button nativeButton={false} role="link" render={<Link to={to} />}>
        {label}
      </Button>
    </div>
  );
}
export function PropertyFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const title =
    isApiError(error) && error.status === 404
      ? '지점을 찾을 수 없습니다'
      : isApiError(error) && error.status === 403
        ? '지점 조회 권한이 없습니다'
        : isApiError(error) && error.status === 503
          ? '지점 서비스를 이용할 수 없습니다'
          : isApiError(error) && error.kind === 'invalid-response'
            ? '지점 정보를 읽지 못했습니다'
            : '지점 정보를 불러오지 못했습니다';
  const retryable = !(isApiError(error) && (error.status === 403 || error.status === 404));
  return (
    <div role="alert" className="p-6">
      <EmptyState
        icon={Building2Icon}
        title={title}
        description="현재 조회 결과를 표시할 수 없습니다."
      />
      {retryable && <Button onClick={onRetry}>다시 시도</Button>}
    </div>
  );
}
